import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { useVesselStore } from '../../../context/VesselContext';
import { NoonReportPowerpacksSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { EngineDataTypeEnum, NoonReportSectionEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getConsumptionCost, hasInvalidFields, hasMissingValues } from '../../../utils/aolFieldsHelper';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import { SubSectionHeader } from '../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { EngineListWrapper, EngineListRow, EngineListHeader, EngineListValuesWrapper } from '../engines-section/EnginesSection.style';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import ConsumptionContainer from '../consumption/ConsumptionContainer';
import useAolItemsHelper from 'src/utils/aol/useAolItemsHelper';

type PowerPackVM = {
    engineNumber: number;
    runningHours?: number;
    consumptionMgo?: number;
    type?: number;
};
type PowerpacksVM = {
    dieselGasoilDrivenPowerpacks: PowerPackVM[];
    electricalDrivenPowerPacks: PowerPackVM[];
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
    consumption?: Consumption[];
};

const initialVM: PowerpacksVM = {
    dieselGasoilDrivenPowerpacks: [],
    electricalDrivenPowerPacks: [],
    consumption: [],
};

const PowerPacksSectionComponent: React.FC = () => {
    const { selectedAolGroup, selectedAolDataItem, aolSyncEvent } = useAolStore();
    const { getVoyageFuelPrice } = useAolItemsHelper();
    const [vm, setVM] = useState<PowerpacksVM>(initialVM);
    const { vessel } = useVesselStore();
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.PowerPacks &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!selectedAolDataItem) return;

        if (!vm.isInitialized) {
            initVM();
        }
    }, [selectedAolDataItem]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(vm?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;

        let dieselGasoilDrivenPowerpacks: PowerPackVM[] = [];
        let electricalDrivenPowerPacks: PowerPackVM[] = [];
        if (noonReport && noonReport.powerpacksSection) {
            dieselGasoilDrivenPowerpacks = noonReport.powerpacksSection.dieselGasoilDrivenPowerpacks.map((n) => {
                return { engineNumber: n.engineNumber, runningHours: n.runningHours, consumptionMgo: n.consumptionMgo, type: n.type };
            });
            electricalDrivenPowerPacks = noonReport.powerpacksSection.electricalDrivenPowerPacks.map((n) => {
                return { engineNumber: n.engineNumber, runningHours: n.runningHours, consumptionMgo: n.consumptionMgo, type: n.type };
            });
        } else {
            const numberOfFuelDrivenPowerPacks =
                vessel?.vesselConfigurationV3?.engineConfiguration?.engineDrivenHydraulicPowerPacks?.numberOfPowerPacks ?? 3;
            const numberOfElectricalDrivenPowerPacks =
                vessel?.vesselConfigurationV3?.engineConfiguration?.hydraulicPowerPacks?.numberOfPowerPacks ?? 6;
            for (let i = 0; i < numberOfFuelDrivenPowerPacks; i++) {
                dieselGasoilDrivenPowerpacks.push({
                    engineNumber: i + 1,
                    type: EngineDataTypeEnum.DieselGasOilDrivenPowerPack,
                    runningHours: 0,
                });
            }
            for (let i = 0; i < numberOfElectricalDrivenPowerPacks; i++) {
                electricalDrivenPowerPacks.push({
                    engineNumber: i + 1,
                    type: EngineDataTypeEnum.ElectricalDrivenPowerPack,
                    runningHours: 0,
                });
            }
        }

        const vm: PowerpacksVM = {
            dieselGasoilDrivenPowerpacks,
            electricalDrivenPowerPacks,
            sectionData: noonReport?.powerpacksSection?.sectionData ?? createSectionData(NoonReportSectionEnum.PowerPacks),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
            consumption: selectedAolDataItem?.noonReport?.powerpacksSection?.consumption ?? [],
        };

        setHasEmptyValues(false);
        setVM({ ...vm });
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        changeObject.dataObject[name] = value ? +value : undefined;
        const voyageFuelPrice = changeObject.extraArgs?.aolItem?.voyageNumber ? getVoyageFuelPrice(changeObject.extraArgs) : null;
        changeObject.dataObject.cost = getConsumptionCost(changeObject.dataObject.fuelType, voyageFuelPrice, +value);

        hasInvalidFields(vm, name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        hasInvalidFields(vm, changeObject.name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const save = () => {
        const ignoreList = [''];
        if (selectedAolDataItem.aolVersion >= 5) ignoreList.push('consumptionMgo');
        let hasMissingValue;
        if (!isDieselGasoilDriven() && isElectricalDriven()) {
            ignoreList.push('dieselGasoilDrivenPowerpacks');
            ignoreList.push('consumptionMgo');
            hasMissingValue = hasMissingValues(vm, ignoreList);
            if (!hasMissingValue) {
                hasMissingValue = vm.electricalDrivenPowerPacks.some((p) => hasMissingValues(p, ignoreList));
            }
        } else if (isDieselGasoilDriven() && !isElectricalDriven()) {
            ignoreList.push('electricalDrivenPowerPacks');
            hasMissingValue = hasMissingValues(vm, ignoreList);
            if (!hasMissingValue) {
                hasMissingValue = vm.dieselGasoilDrivenPowerpacks.some((p) => hasMissingValues(p, ignoreList));
            }
        } else {
            hasMissingValue = hasMissingValues(vm, ignoreList);
            if (!hasMissingValue) {
                hasMissingValue = vm.electricalDrivenPowerPacks.some((p) => hasMissingValues(p, ignoreList));
            }
            if (!hasMissingValue) {
                hasMissingValue = vm.dieselGasoilDrivenPowerpacks.some((p) => hasMissingValues(p, ignoreList));
            }
        }
        if (hasMissingValue) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportPowerpacksSectionFactory().createBM(vm);
            if (!data.sectionData || data.sectionData.section === 0)
                data.sectionData = {
                    noonReportId: 0,
                    section: NoonReportSectionEnum.PowerPacks,
                    isApproved: false,
                    isCompleted: false,
                    role: 1,
                };
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!vm.hasInvalidFields) {
                saveSection(data);
            }
        }
    };

    const isDieselGasoilDriven = () =>
        vessel?.vesselConfigurationV3?.engineConfiguration?.engineDrivenHydraulicPowerPacks?.numberOfPowerPacks &&
        vessel?.vesselConfigurationV3?.engineConfiguration?.engineDrivenHydraulicPowerPacks?.numberOfPowerPacks > 0 &&
        vm.dieselGasoilDrivenPowerpacks;

    const isElectricalDriven = () =>
        vessel?.vesselConfigurationV3?.engineConfiguration?.hydraulicPowerPacks?.numberOfPowerPacks &&
        vessel?.vesselConfigurationV3?.engineConfiguration?.hydraulicPowerPacks?.numberOfPowerPacks > 0 &&
        vm.electricalDrivenPowerPacks;

    const getPowerPackLabel = (i: number): string => {
        return 'Power pack ' + (i + 1).toString();
    };

    const addConsumptionRow = (consumption: Consumption[]) => {
        consumption.forEach((c) => {
            vm.consumption.push(c);
        });

        setVM({ ...vm });
    };

    return (
        <>
            {vm && (
                <>
                    {!!isDieselGasoilDriven() && (
                        <>
                            <SubSectionHeader>Diesel/Gasoil driven power packs</SubSectionHeader>
                            <EngineListWrapper>
                                {selectedAolDataItem.aolVersion >= 5 &&
                                    vm?.dieselGasoilDrivenPowerpacks &&
                                    vm?.dieselGasoilDrivenPowerpacks?.length > 0 && (
                                        <ConsumptionContainer
                                            aolItem={selectedAolDataItem}
                                            engineType={EngineDataTypeEnum.DieselGasOilDrivenPowerPack}
                                            consumption={vm?.consumption}
                                            isReadOnly={isReadOnly}
                                            onChange={onInputChange}
                                            addConsumptionRow={addConsumptionRow}
                                        />
                                    )}
                                <EngineListRow>
                                    <EngineListHeader />
                                    <EngineListValuesWrapper gridCols={selectedAolDataItem.aolVersion >= 5 ? 1 : 2}>
                                        {!selectedAolDataItem?.aolVersion ||
                                            (selectedAolDataItem.aolVersion < 5 && <EngineListHeader>MGO</EngineListHeader>)}
                                        <EngineListHeader>Running hours</EngineListHeader>
                                    </EngineListValuesWrapper>
                                </EngineListRow>

                                {vm.dieselGasoilDrivenPowerpacks
                                    .sort((a, b) => a.engineNumber - b.engineNumber)
                                    .map((dieselGasoilDrivenPowerpack, i) => (
                                        <EngineListRow key={`${dieselGasoilDrivenPowerpack.type}_${i}`}>
                                            <div>{getPowerPackLabel(i)}</div>
                                            <EngineListValuesWrapper gridCols={selectedAolDataItem.aolVersion >= 5 ? 1 : 2}>
                                                {!selectedAolDataItem?.aolVersion ||
                                                    (selectedAolDataItem.aolVersion < 5 && (
                                                        <OdfjellInputComponent
                                                            type={'number'}
                                                            isReadOnly={isReadOnly}
                                                            value={dieselGasoilDrivenPowerpack.consumptionMgo}
                                                            onChange={onInputChange}
                                                            name={'consumptionMgo'}
                                                            dataObject={dieselGasoilDrivenPowerpack}
                                                            min={0}
                                                            max={100}
                                                        />
                                                    ))}
                                                <OdfjellInputComponent
                                                    type={'time'}
                                                    isReadOnly={isReadOnly}
                                                    value={dieselGasoilDrivenPowerpack.runningHours}
                                                    onTimeInputChange={onTimeInputChange}
                                                    name={'runningHours'}
                                                    dataObject={dieselGasoilDrivenPowerpack}
                                                    max={selectedAolGroup?.aolHours}
                                                />
                                            </EngineListValuesWrapper>
                                        </EngineListRow>
                                    ))}
                            </EngineListWrapper>
                        </>
                    )}
                    {!!isElectricalDriven() && (
                        <>
                            <SubSectionHeader>Electrical driven power packs</SubSectionHeader>
                            <EngineListWrapper>
                                {vm.electricalDrivenPowerPacks
                                    .sort((a, b) => a.engineNumber - b.engineNumber)
                                    .map((electricalDrivenPowerPack, i) => (
                                        <div key={`${electricalDrivenPowerPack.type}_${i}`}>
                                            <OdfjellInputComponent
                                                description={getPowerPackLabel(i)}
                                                type={'time'}
                                                isReadOnly={isReadOnly}
                                                value={electricalDrivenPowerPack.runningHours}
                                                onTimeInputChange={onTimeInputChange}
                                                name={'runningHours'}
                                                dataObject={electricalDrivenPowerPack}
                                                max={selectedAolGroup?.aolHours}
                                            ></OdfjellInputComponent>
                                        </div>
                                    ))}
                            </EngineListWrapper>
                        </>
                    )}
                </>
            )}
            {hasEmptyValues && <ErrorText>You must complete the section before saving.</ErrorText>}
        </>
    );
};

export default PowerPacksSectionComponent;
